import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import type { ImmutableTrip } from 'src/types/trips/Trips';
import { open } from '../../../common/ducks/zippy';
import useCustomOfferExpiry from '../../../common/hooks/useCustomOfferExpiry';

import { TRIP_STATES } from '../../constants';
import { CANCEL_REQUESTED_CHANGES } from '../TripMenu/actions';
import ClearButton from '../../../common/components/ClearButton';
import UserBlock from '../presentation/UserBlock';

type RenterChangesRequestedMessageProps = {
  trip: ImmutableTrip;
};

const RenterChangesRequestedMessage = ({ trip }: RenterChangesRequestedMessageProps) => {
  const tripState = trip.getIn(['state', 'state']);
  const {
    confirmationPeriod,
    hasCustomExpiry,
    offerExpired,
  } = useCustomOfferExpiry(trip);
  // This is redefined here because `renderCustomExpiry` from useCustomOfferExpiry
  // rely on different set of trip states
  const renderCustomExpiry = hasCustomExpiry && tripState === TRIP_STATES.RENTER_CHANGES_REQUESTED;
  const { name: modalName } = CANCEL_REQUESTED_CHANGES;
  const dispatch = useDispatch();
  // This dispatches an action to open the CANCEL_REQUESTED_CHANGES modal,
  // which is defined and rendered by TripMenu when TripMenu is mounted, with a form to
  // cancel the change request and keep the previous offer valid.
  const openModal = useCallback(
    () => dispatch(open(modalName)),
    [dispatch, modalName],
  );

  return (
    <UserBlock user={trip.get('owner')}>
      <strong>
        {trip.getIn(['owner', 'first_name'])} will be prompted to get back to you soon
        about the changes you requested to this trip.
      </strong>
      {renderCustomExpiry && (
        offerExpired
          ? ' Your previous offer has expired.'
          : (
            <>
              {' You can '}
              <ClearButton
                classNameModifier="inline"
                onClick={openModal}
              >
                cancel the change request
              </ClearButton>
              {' '}and book the previous offer.
              The offer will be valid for {confirmationPeriod}.
            </>
          )
      )}
    </UserBlock>
  );
};

export default RenterChangesRequestedMessage;
